import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Search, Calendar, Users, MapPin, Copy, Check, Share2 } from "lucide-react";
import api from "@/lib/api";

export default function MyBookingsPage() {
  const [email, setEmail] = useState("");
  const [bookings, setBookings] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const handleLookup = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/bookings/lookup", { email });
      setBookings(Array.isArray(res.data) ? res.data : []);
      setSearched(true);
    } catch {
      toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const copyReferral = (code) => {
    const msg = `Book an adventure in Saint Lucia with Keyz's Epic Adventure Tours! Use my code ${code} for 15% off: ${window.location.origin}/tours`;
    navigator.clipboard.writeText(msg).then(() => {
      setCopiedCode(code);
      toast.success("Referral link copied!");
      setTimeout(() => setCopiedCode(null), 2000);
    }).catch(() => toast.error("Copy failed"));
  };

  const shareWhatsApp = (code) => {
    const msg = encodeURIComponent(`Book an adventure in Saint Lucia with Keyz's Epic Adventure Tours! Use my code ${code} for 15% off: ${window.location.origin}/tours`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const statusColor = {
    confirmed: "bg-canopy/10 text-canopy",
    cancelled: "bg-red-100 text-red-700",
    pending: "bg-sandy/20 text-sandy",
  };

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      <div className="pt-28 pb-24 px-6 max-w-3xl mx-auto">
        <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-3">Your Adventures</p>
        <h1 className="font-cormorant text-4xl sm:text-5xl font-light text-midnight mb-6" data-testid="my-bookings-title">
          My Bookings
        </h1>
        <p className="font-manrope text-sm text-midnight/50 mb-8">
          Look up your bookings and share referral codes with friends.
        </p>

        {/* Email Lookup */}
        <div className="flex gap-3 mb-12" data-testid="booking-lookup-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-midnight/30" />
            <Input
              data-testid="lookup-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your booking email"
              className="pl-10 font-manrope text-sm"
              onKeyDown={(e) => e.key === "Enter" && handleLookup()}
            />
          </div>
          <Button
            data-testid="lookup-submit-btn"
            onClick={handleLookup}
            disabled={loading}
            className="rounded-full bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-sm px-6"
          >
            {loading ? "Searching..." : "Find Bookings"}
          </Button>
        </div>

        {/* Results */}
        {searched && bookings.length === 0 && (
          <div className="text-center py-16" data-testid="no-bookings-found">
            <p className="font-cormorant text-2xl text-midnight/30 mb-2">No bookings found</p>
            <p className="font-manrope text-sm text-midnight/30">Make sure you entered the same email used at checkout.</p>
          </div>
        )}

        {bookings.length > 0 && (
          <div className="space-y-6" data-testid="bookings-list">
            <p className="font-manrope text-sm text-midnight/50">{bookings.length} booking{bookings.length !== 1 ? 's' : ''} found</p>
            {bookings.map((b) => (
              <div key={b.booking_id} className="bg-white border border-midnight/5 p-6" data-testid={`my-booking-${b.booking_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-cormorant text-xl text-midnight">{b.tour_title}</h3>
                    <p className="font-manrope text-xs text-midnight/40 mt-1">Ref: {b.booking_id?.slice(0, 8)}</p>
                  </div>
                  <Badge className={`font-manrope text-xs ${statusColor[b.status] || ""}`}>{b.status}</Badge>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-sandy" />
                    <span className="font-manrope text-sm text-midnight/70">{b.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-sandy" />
                    <span className="font-manrope text-sm text-midnight/70">{b.guests} guest{b.guests !== 1 ? 's' : ''}</span>
                  </div>
                  <div>
                    <span className="font-manrope text-sm font-medium text-midnight">${b.total_price?.toFixed(2)}</span>
                    {b.discount_type && (
                      <span className="font-manrope text-[10px] text-canopy ml-1">-{b.discount_percent}%</span>
                    )}
                  </div>
                  <div>
                    <span className="font-manrope text-xs text-midnight/30 uppercase">{b.payment_method}</span>
                  </div>
                </div>

                {/* Referral Share Section */}
                {b.referral_code && b.status === "confirmed" && (
                  <>
                    <Separator className="my-4" />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-manrope text-xs uppercase tracking-widest text-midnight/40 mb-1">Your Referral Code</p>
                        <p className="font-manrope text-sm font-semibold text-sandy tracking-wider">{b.referral_code}</p>
                        <p className="font-manrope text-[10px] text-midnight/30 mt-0.5">Friends get 15% off when they use your code</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          data-testid={`copy-referral-${b.referral_code}`}
                          onClick={() => copyReferral(b.referral_code)}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-midnight/10 text-midnight/50 hover:border-sandy hover:text-sandy transition-colors duration-200"
                        >
                          {copiedCode === b.referral_code ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          <span className="font-manrope text-xs">{copiedCode === b.referral_code ? "Copied" : "Copy"}</span>
                        </button>
                        <button
                          data-testid={`share-whatsapp-${b.referral_code}`}
                          onClick={() => shareWhatsApp(b.referral_code)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/20 hover:bg-[#25D366]/20 transition-colors duration-200"
                        >
                          <Share2 className="w-3 h-3" />
                          <span className="font-manrope text-xs">WhatsApp</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
